import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import PoweredByFooter from "../components/PoweredByFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  ArrowLeft, FileText, ClipboardList, Upload, Trash2, 
  Plus, Clock, User, ExternalLink, Link2, FileUp
} from "lucide-react";

export default function VigtigeInfo({ user }) {
  const navigate = useNavigate();
  const { eventId: urlEventId } = useParams();
  const eventId = urlEventId || localStorage.getItem("selectedEventId");
  const eventName = localStorage.getItem("selectedEventName") || "Event";
  const token = localStorage.getItem("token");
  
  const [activeTab, setActiveTab] = useState('fraktioner');
  const [documents, setDocuments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [moduleStatus, setModuleStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Note state
  const [newNote, setNewNote] = useState('');
  
  // Evaluation link state
  const [evaluationLink, setEvaluationLink] = useState('');
  const [editingLink, setEditingLink] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  
  const isAdmin = user?.role === 'admin';
  const isLeader = user?.role === 'leader' || user?.role === 'admin';

  useEffect(() => {
    if (eventId) {
      loadDocuments();
      loadNotes();
      loadModuleStatus();
    }
  }, [eventId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events/${eventId}/info-documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(res.data || []);
    } catch (error) {
      console.error("Kunne ikke hente dokumenter", error);
    } finally {
      setLoading(false);
    }
  };

  const loadNotes = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/evaluation-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotes(res.data || []);
    } catch (error) {
      console.error("Kunne ikke hente noter", error);
    }
  };

  const loadModuleStatus = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/info-module-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModuleStatus(res.data);
      setEvaluationLink(res.data?.evaluation_link || '');
    } catch (error) {
      console.error("Kunne ikke hente modulstatus", error);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadTitle.trim()) {
      toast.error("Vælg en PDF-fil og indtast en titel");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadTitle);
      formData.append('category', activeTab);

      await axios.post(`${API}/events/${eventId}/info-documents/upload-pdf`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success("PDF uploadet!");
      setShowUpload(false);
      setUploadTitle('');
      setUploadFile(null);
      loadDocuments();
      loadModuleStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved upload");
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/info-documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Dokument slettet");
      loadDocuments();
    } catch (error) {
      toast.error("Fejl ved sletning");
    }
  };

  const saveNote = async () => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API}/events/${eventId}/evaluation-notes`, 
        { content: newNote },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Note gemt");
      setNewNote('');
      loadNotes();
    } catch (error) {
      toast.error("Fejl ved gemning af note");
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/evaluation-notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Note slettet");
      loadNotes();
    } catch (error) {
      toast.error("Fejl ved sletning");
    }
  };

  const saveEvaluationLink = async () => {
    try {
      await axios.put(`${API}/events/${eventId}/evaluation-link`, 
        { link: linkInput },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setEvaluationLink(linkInput);
      setEditingLink(false);
      toast.success("Evalueringslink gemt");
    } catch (error) {
      toast.error("Fejl ved gemning af link");
    }
  };

  const filteredDocs = documents.filter(d => d.category === activeTab);

  const tabs = [
    { id: 'fraktioner', label: 'Fraktionsoversigter', icon: FileText },
    { id: 'arbejdsbeskrivelser', label: 'Arbejdsbeskrivelser', icon: FileText },
    { id: 'evaluering', label: 'Evaluering', icon: ClipboardList },
  ];

  const renderDocument = (doc) => (
    <div key={doc.id} className="bg-card border border-border rounded-lg overflow-hidden" data-testid={`doc-${doc.id}`}>
      <div className="p-4 border-b border-border flex items-center justify-between bg-muted/30">
        <div>
          <h3 className="font-bold">{doc.title}</h3>
          <p className="text-xs text-muted-foreground">
            {doc.original_filename} - Uploadet af {doc.uploaded_by}
            {doc.content_type !== 'pdf' && <span className="ml-2 text-yellow-400">(Gammelt format - upload som PDF for bedre visning)</span>}
          </p>
        </div>
        {isLeader && (
          <Button 
            size="sm" 
            variant="ghost" 
            className="text-red-400 hover:bg-red-500/10" 
            onClick={() => deleteDocument(doc.id)}
            data-testid={`delete-doc-${doc.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      {doc.content_type === 'pdf' ? (
        <div className="bg-neutral-100" style={{ height: '700px' }}>
          <iframe
            src={doc.content}
            title={doc.title}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          />
        </div>
      ) : doc.content_type === 'html' ? (
        <div className="overflow-auto max-h-[500px] bg-white">
          <div className="p-4 min-w-[600px] text-black" dangerouslySetInnerHTML={{ __html: doc.content }} />
        </div>
      ) : doc.content_type === 'image' ? (
        <div className="p-4 bg-white flex justify-center">
          <img src={doc.content} alt={doc.title} className="max-w-full" />
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-card border-b border-border">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate("/")}
                data-testid="back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tilbage
              </Button>
              <div>
                <h1 className="text-xl font-bold">Vigtige Informationer</h1>
                <p className="text-sm text-muted-foreground">{eventName}</p>
              </div>
            </div>
            
            {moduleStatus?.updated_at && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Opdateret: {new Date(moduleStatus.updated_at).toLocaleDateString('da-DK')}
              </div>
            )}
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="container mx-auto px-4 pb-3">
          <div className="flex gap-2 flex-wrap">
            {tabs.map(tab => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
              </Button>
            ))}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-5xl">
        {/* PDF Upload Section (for fraktioner & arbejdsbeskrivelser) */}
        {isLeader && activeTab !== 'evaluering' && (
          <div className="mb-6 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {filteredDocs.length} PDF-dokument{filteredDocs.length !== 1 ? 'er' : ''} uploadet
            </p>
            <Button onClick={() => setShowUpload(true)} data-testid="upload-pdf-btn">
              <FileUp className="h-4 w-4 mr-2" />
              Upload PDF
            </Button>
          </div>
        )}

        {/* Fraktionsoversigter & Arbejdsbeskrivelser (same rendering) */}
        {(activeTab === 'fraktioner' || activeTab === 'arbejdsbeskrivelser') && (
          <div className="space-y-6">
            {loading ? (
              <div className="text-center py-16 text-muted-foreground">Indlæser...</div>
            ) : filteredDocs.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Ingen {activeTab === 'fraktioner' ? 'fraktionsoversigter' : 'arbejdsbeskrivelser'} uploadet endnu</p>
                {isLeader && <p className="text-sm mt-2">Upload PDF-filer for at komme i gang</p>}
              </div>
            ) : (
              filteredDocs.map(doc => renderDocument(doc))
            )}
          </div>
        )}

        {/* Evaluering Tab */}
        {activeTab === 'evaluering' && (
          <div className="space-y-6">
            {/* SurveyMonkey / Evaluation Link */}
            <div className="bg-card border border-border rounded-lg p-5" data-testid="evaluation-link-section">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="h-5 w-5 text-indigo-400" />
                <h3 className="font-bold">Evalueringslink</h3>
              </div>
              
              {evaluationLink && !editingLink ? (
                <div className="flex items-center gap-3 p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                  <ExternalLink className="h-4 w-4 text-indigo-400 shrink-0" />
                  <a 
                    href={evaluationLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-indigo-400 hover:underline truncate flex-1"
                    data-testid="evaluation-link"
                  >
                    {evaluationLink}
                  </a>
                  {isLeader && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => { setLinkInput(evaluationLink); setEditingLink(true); }}
                      data-testid="edit-evaluation-link-btn"
                    >
                      Rediger
                    </Button>
                  )}
                </div>
              ) : isLeader ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Indsæt et link til din evaluering (fx SurveyMonkey, Google Forms, etc.)
                  </p>
                  <Input
                    value={linkInput}
                    onChange={(e) => setLinkInput(e.target.value)}
                    placeholder="https://www.surveymonkey.com/r/dit-skema"
                    data-testid="evaluation-link-input"
                  />
                  <div className="flex gap-2">
                    <Button 
                      onClick={saveEvaluationLink} 
                      disabled={!linkInput.trim()}
                      data-testid="save-evaluation-link-btn"
                    >
                      Gem link
                    </Button>
                    {editingLink && (
                      <Button variant="outline" onClick={() => setEditingLink(false)}>
                        Annuller
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Intet evalueringslink er indsat endnu.
                </p>
              )}
            </div>

            {/* Skriv Note */}
            <div className="bg-card border border-border rounded-lg p-5" data-testid="notes-section">
              <h3 className="font-bold mb-3">Skriv en note</h3>
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Skriv dine tanker, feedback eller forslag..."
                className="mb-3"
                rows={4}
                data-testid="note-input"
              />
              <Button onClick={saveNote} disabled={!newNote.trim()} data-testid="save-note-btn">
                <Plus className="h-4 w-4 mr-2" />
                Gem note
              </Button>
            </div>

            {/* Existing Notes */}
            <div>
              <h3 className="font-bold mb-3">Alle noter ({notes.length})</h3>
              {notes.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Ingen noter endnu</p>
              ) : (
                <div className="space-y-3">
                  {notes.map(note => (
                    <div key={note.id} className="bg-card border border-border rounded-lg p-4" data-testid={`note-${note.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="whitespace-pre-wrap">{note.content}</p>
                          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
                            <User className="h-3 w-3" />
                            {note.user_name} - {new Date(note.created_at).toLocaleDateString('da-DK')}
                          </p>
                        </div>
                        {(note.user_id === user?.id || isAdmin) && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-400" 
                            onClick={() => deleteNote(note.id)}
                            data-testid={`delete-note-${note.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <PoweredByFooter />
      </main>

      {/* Upload Dialog */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md" data-testid="upload-dialog">
            <h3 className="font-bold text-lg mb-2">Upload PDF</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Upload en PDF-fil til {activeTab === 'fraktioner' ? 'fraktionsoversigter' : 'arbejdsbeskrivelser'}.
              Du kan uploade flere dokumenter.
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Titel *</label>
                <Input
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="F.eks. Fraktionsoversigt 2026"
                  data-testid="upload-title-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">PDF-fil *</label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  data-testid="upload-file-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Kun .pdf filer
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => { setShowUpload(false); setUploadFile(null); setUploadTitle(''); }}>
                Annuller
              </Button>
              <Button 
                onClick={handleUpload} 
                disabled={uploading || !uploadFile || !uploadTitle.trim()}
                data-testid="confirm-upload-btn"
              >
                {uploading ? 'Uploader...' : 'Upload PDF'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
